import apiService from '../index';
import fileService from '../utils/fileService';

class StatisticsManager {
  constructor() {
    this.apiService = apiService;
    this.config = this.apiService.config;
    this.filePrefix = 'file_stats_';
  }

  // Создание ключа для хранения файла статистики
  _createFileKey(leagueId, season, teamId) {
    return `${this.filePrefix}${leagueId}_${season}_${teamId}`;
  }

  // Сохранение данных статистики в файл
  saveStatisticsToFile(leagueId, season, teamId, data) {
    try {
      if (!leagueId || !season || !teamId || !data) {
        console.error('❌ Неверные параметры для сохранения данных статистики');
        return false;
      }

      // Создаем ключ для хранения
      const fileKey = this._createFileKey(leagueId, season, teamId);

      // Добавляем метку времени к данным
      const dataWithTimestamp = {
        ...data,
        timestamp: data.timestamp || new Date().toISOString(),
        fromFile: true
      };

      // Записываем данные в localStorage через fileService
      return fileService.saveData(fileKey, dataWithTimestamp);
    } catch (error) {
      console.error(`❌ Ошибка при сохранении данных статистики:`, error);
      return false;
    }
  }

  // Загрузка данных статистики из файла
  loadStatisticsFromFile(leagueId, season, teamId) {
    try {
      if (!leagueId || !season || !teamId) {
        console.error('❌ Неверные параметры для загрузки данных статистики');
        return null;
      }

      // Создаем ключ для хранения
      const fileKey = this._createFileKey(leagueId, season, teamId);

      // Проверяем существование файла
      if (!fileService.fileExists(fileKey)) {
        console.log(`❌ "Файл" с данными статистики для лиги ${leagueId}, сезона ${season}, команды ${teamId} не найден`);
        return null;
      }

      // Загружаем данные из файла
      const data = fileService.loadData(fileKey);
      
      if (!data) {
        console.log(`❌ Не удалось загрузить данные из "файла" ${fileKey}`);
        return null;
      }
      
      console.log(`✅ Данные статистики для лиги ${leagueId}, сезона ${season}, команды ${teamId} загружены из "файла"`);

      // Проверяем актуальность данных (не старше 7 дней)
      if (data && data.timestamp) {
        const dataDate = new Date(data.timestamp);
        const now = new Date();
        const diffDays = Math.ceil((now - dataDate) / (1000 * 60 * 60 * 24));
        
        if (diffDays > 7) {
          console.log(`⚠️ Данные статистики для лиги ${leagueId}, сезона ${season}, команды ${teamId} устарели (${diffDays} дней)`);
          return null;
        }
      }

      return data;
    } catch (error) {
      console.error(`❌ Ошибка при загрузке данных статистики из "файла":`, error);
      return null;
    }
  }

  // Очистка файла статистики
  clearStatisticsFile(leagueId, season, teamId) {
    try {
      if (!leagueId || !season || !teamId) {
        console.error('❌ Неверные параметры для очистки "файла" статистики');
        return false;
      }

      // Создаем ключ для хранения
      const fileKey = this._createFileKey(leagueId, season, teamId);

      // Удаляем файл
      if (fileService.fileExists(fileKey)) {
        fileService.deleteFile(fileKey);
        console.log(`✅ "Файл" статистики для лиги ${leagueId}, сезона ${season}, команды ${teamId} успешно удален`);
        return true;
      } else {
        console.log(`⚠️ "Файл" статистики для лиги ${leagueId}, сезона ${season}, команды ${teamId} не найден`);
        return false;
      }
    } catch (error) {
      console.error(`❌ Ошибка при удалении "файла" статистики:`, error);
      return false;
    }
  }

  // Очистка всех файлов статистики
  clearAllStatisticsFiles() {
    try {
      // Получаем список всех ключей в localStorage
      const files = fileService.getCacheFiles(true);
      let count = 0;
      
      // Удаляем каждый файл статистики
      files.forEach(key => {
        if (key.startsWith(this.filePrefix)) {
          fileService.deleteFile(key.replace(fileService.filePrefix, ''));
          count++;
        }
      });

      if (count > 0) {
        console.log(`✅ Удалено ${count} файлов статистики`);
        return true;
      } else {
        console.log('⚠️ Файлы статистики не найдены');
        return false;
      }
    } catch (error) {
      console.error(`❌ Ошибка при удалении файлов статистики:`, error);
      return false;
    }
  }

  async fetchStatistics(leagueId, season, teamId) {
    try {
      if (!leagueId || !season || !teamId) {
        console.error('❌ Ошибка: не указаны параметры для получения статистики');
        return {
          status: 'error',
          message: 'Не указаны лига, сезон или команда',
          endpoint: 'statistics'
        };
      }
      
      console.log(`Fetching statistics data for league ID ${leagueId}, season ${season}, team ID ${teamId} using StatisticsManager...`);
      
      // Сперва проверяем наличие данных в файле
      const fileData = this.loadStatisticsFromFile(leagueId, season, teamId);
      if (fileData) {
        console.log(`Данные статистики для лиги ${leagueId}, сезона ${season}, команды ${teamId} загружены из файла`);
        return {
          status: 'success',
          data: fileData,
          source: { 
            fromCache: false, 
            fromFile: true, 
            timestamp: fileData.timestamp || new Date().toISOString() 
          },
          endpoint: 'statistics',
          params: { leagueId, season, teamId }
        };
      }
      
      // Если данных в файле нет, проверяем кэш и запрашиваем через API
      const response = await this.apiService.getStatistics(leagueId, season, teamId);
      console.log('Statistics response:', response);
      
      // Если получили успешный ответ от API, сохраняем в файл
      if (response && response.data && response.data.response) {
        // Текущее время для метки в данных
        const timestamp = new Date().toISOString();
        
        // Добавляем timestamp к данным
        if (!response.data.timestamp) {
          response.data.timestamp = timestamp;
        }
        
        // Сохраняем в файл
        this.saveStatisticsToFile(leagueId, season, teamId, response.data);
        
        return {
          status: 'success',
          data: response.data,
          source: response.fromCache 
            ? { fromCache: true, fromFile: false, timestamp: response.data.timestamp || timestamp } 
            : { fromCache: false, fromFile: false, timestamp },
          endpoint: 'statistics',
          params: { leagueId, season, teamId }
        };
      }
      
      return {
        status: 'success',
        data: response,
        endpoint: 'statistics',
        params: { leagueId, season, teamId }
      };
    } catch (error) {
      console.error('Error in StatisticsManager:', error);
      return {
        status: 'error',
        message: error.message,
        endpoint: 'statistics'
      };
    }
  }
  
  // Получение списка всех файлов статистики
  getStatisticsFilesList() {
    try {
      // Получаем список всех ключей в localStorage
      const filesList = [];
      const files = fileService.getCacheFiles(true);
      
      files.forEach(key => {
        if (key.startsWith(this.filePrefix)) {
          // Извлекаем информацию из ключа
          const parts = key.replace(this.filePrefix, '').split('_');
          if (parts.length === 3) {
            const [leagueId, season, teamId] = parts;
            
            // Получаем данные
            const fileData = fileService.loadData(key.replace(fileService.filePrefix, ''));
            let timestamp = null;
            let size = fileData ? JSON.stringify(fileData).length : 0;
            
            if (fileData && fileData.timestamp) {
              timestamp = fileData.timestamp;
            }
            
            filesList.push({
              key: key.replace(fileService.filePrefix, ''),
              leagueId: parseInt(leagueId),
              season: parseInt(season),
              teamId: parseInt(teamId),
              size,
              timestamp,
              modified: timestamp ? new Date(timestamp) : new Date()
            });
          }
        }
      });

      console.log(`✅ Получен список файлов статистики (${filesList.length}):`, filesList);
      return filesList;
    } catch (error) {
      console.error(`❌ Ошибка при получении списка файлов статистики:`, error);
      return [];
    }
  }
}

export default new StatisticsManager(); 